import { useState, useCallback, ChangeEvent } from 'react';
import { validateFileType, validateFileSize } from '../utils/uploadHelpers';

interface UseFileUploadConfig {
  multiple?: boolean;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  onError?: (error: string) => void;
}

export interface FileUploadState {
  files: File[];
  uploadProgress: number;
  uploadStep: string;
  uploadError: string | null;
  isDragging: boolean;
}

export interface FileUploadActions {
  handleFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleRemoveFile: (index: number) => void;
  clearFiles: () => void;
  setUploadProgress: (progress: number) => void;
  setUploadStep: (step: string) => void;
  setUploadError: (error: string | null) => void;
}

export function useFileUpload(config: UseFileUploadConfig = {}) {
  const {
    multiple = false,
    acceptedTypes = ['.csv', '.xlsx', '.json', '.pdf'],
    maxSizeMB = 50,
    onError,
  } = config;

  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!validateFileType(file, acceptedTypes)) {
        return `Invalid file type. Accepted: ${acceptedTypes.join(', ')}`;
      }
      if (!validateFileSize(file, maxSizeMB)) {
        return `File too large. Max size: ${maxSizeMB}MB`;
      }
      return null;
    },
    [acceptedTypes, maxSizeMB]
  );

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);

      if (selectedFiles.length === 0) return;

      // Validate all files
      const validFiles: File[] = [];
      const errors: string[] = [];

      selectedFiles.forEach(file => {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      });

      // Report errors if any
      if (errors.length > 0) {
        const errorMessage = errors.join('\n');
        setUploadError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      // Add valid files
      if (multiple) {
        setFiles(prev => [...prev, ...validFiles]);
      } else {
        setFiles([validFiles[0]]);
      }

      setUploadError(null);
    },
    [multiple, validateFile, onError]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);

      if (droppedFiles.length === 0) return;

      // Validate all files
      const validFiles: File[] = [];
      const errors: string[] = [];

      droppedFiles.forEach(file => {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      });

      // Report errors if any
      if (errors.length > 0) {
        const errorMessage = errors.join('\n');
        setUploadError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      // Add valid files
      if (multiple) {
        setFiles(prev => [...prev, ...validFiles]);
      } else {
        setFiles([validFiles[0]]);
      }

      setUploadError(null);
    },
    [multiple, validateFile, onError]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setUploadProgress(0);
    setUploadStep('');
    setUploadError(null);
  }, []);

  const state: FileUploadState = {
    files,
    uploadProgress,
    uploadStep,
    uploadError,
    isDragging,
  };

  const actions: FileUploadActions = {
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleRemoveFile,
    clearFiles,
    setUploadProgress,
    setUploadStep,
    setUploadError,
  };

  return {
    ...state,
    ...actions,
  };
}